// imports
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Collapse, Typography } from '@mui/material';

// local imports
import { mapActions, RootState } from '../../../../store';
import CustomInput from '../../CustomInput';
import ColorPickerPopover from '../../ColorPickerPopover';
import StyledSwitch from '../../StyledSwitch';
import {
  isMVTAdditionalLayer,
  watershedBorderColor,
} from '../../../../configs/additionalLayers';
import { getAriaLabel } from '../../../../utils/ariaLabels';
import { LINK_BLUE } from '../../../../constants/colors';
import {
  hucLayerIds,
  getActiveHucLayerId,
  getHucLevelForZoom,
  HucLayerId,
} from '../../../../utils/hucZoomLevels';

/**
 * WatershedAdditionalLayer
 * Adapted from AdditionalLayer component
 *
 * Renders a container with layer name and toggle switch.
 * When layer is active, expanded view contains an opacity slider and border color/width inputs.
 *
 * @returns {JSX.Element} The rendered additional layer component.
 */
export default function WatershedAdditionalLayer() {
  const dispatch = useDispatch();

  // Derive the active HUC layer ID from zoom — this selector only causes
  // re-renders when the HUC level actually changes (at zoom breakpoints),
  // not on every fractional zoom change during pan/zoom.
  const layerId = useSelector((state: RootState) =>
    getActiveHucLayerId(state.map.viewState.zoom)
  );

  // Initialize toggled state from URL if watersheds are in addlayers parameter
  const [toggled, setToggled] = useState<boolean>(() => {
    // Check URL for any watershed layers on initial load
    const url = new URL(window.location.href);
    const addlayersParam = url.searchParams.get('addlayers');
    if (addlayersParam) {
      const layerIds = addlayersParam.split(',').map((id) => id.trim());
      return layerIds.some((id) => hucLayerIds.includes(id as HucLayerId));
    }
    return false;
  });

  // Switch for turning on/off huc layer
  const handleToggle = () => {
    setToggled((toggled) => !toggled);
  };

  // Update huc layer based on zoom level
  useEffect(() => {
    // Show the correct layer
    dispatch(mapActions.setActive({ id: layerId, active: toggled }));

    // Hide all other huc layers
    hucLayerIds
      .filter((id) => id !== layerId)
      .forEach((id) => dispatch(mapActions.setActive({ id, active: false })));
  }, [layerId, toggled, dispatch]);

  const zoom = useSelector((state: RootState) => state.map.viewState.zoom);

  // layer info
  const layer = useSelector(
    (state: RootState) =>
      state.map.layers[layerId as keyof RootState['map']['layers']]
  );

  // use local state to capture border color value
  const [borderColorValue, setBorderColorValue] =
    useState<string>(watershedBorderColor);

  useEffect(() => {
    hucLayerIds.forEach((id) =>
      dispatch(
        mapActions.setBorderColor({
          id: id,
          borderColor: borderColorValue,
        })
      )
    );
  }, [borderColorValue, dispatch]);

  const handleBorderWidthChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value;
    // allow empty string to clear the input
    if (newValue === '') {
      hucLayerIds.forEach((id) =>
        dispatch(mapActions.setBorderWidth({ id: id, borderWidth: '' }))
      );
      return;
    }
    // Check if the new value is a number and greater than or equal to 1 and less than 100
    if (
      isNaN(Number(newValue)) ||
      Number(newValue) < 1 ||
      Number(newValue) > 99
    ) {
      return;
    }
    hucLayerIds.forEach((id) =>
      dispatch(
        mapActions.setBorderWidth({ id: id, borderWidth: Number(newValue) })
      )
    );
  };

  const layerName = 'Watersheds';

  return (
    <Box>
      <Box
        key={layerId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: 2,
          pb: 1
        }}
      >
        <Typography
          component="span"
          variant="body1"
          data-testid="watershed-additional-layer-name"
          sx={{ fontWeight: 'bold', fontSize: '1.125rem' }}
        >
          {layerName}
        </Typography>
        <StyledSwitch
          edge="end"
          onChange={handleToggle}
          checked={toggled}
          slotProps={{
            input: {
              id: 'layer-toggle-watersheds',
              'aria-label': getAriaLabel(layerName),
              role: 'switch',
            },
          }}
        />
      </Box>
      <Collapse in={toggled} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box>
                <Typography component="span" variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#555', letterSpacing: '0.05em' }}>
                  Border
                </Typography>
                <Box
                  id="border-container"
                  sx={{
                    display: 'flex',
                    width: '100%',
                    gap: '0.5rem',
                    alignItems: 'center',
                  }}
                >
                  <ColorPickerPopover
                    color={borderColorValue}
                    onChange={setBorderColorValue}
                    data-testid="watershed-border-color-picker"
                    aria-label="Border color"
                  />
                  <CustomInput
                    data-testid="watershed-border-width-picker"
                    value={
                      isMVTAdditionalLayer(layer) ? layer?.borderWidth || '' : ''
                    }
                    onChange={handleBorderWidthChange}
                    width="80px"
                    suffix="px"
                    type="number"
                    min="1"
                    max="99"
                    aria-label="Border width in pixels"
                  />
                </Box>
              </Box>
              <Box>
                <Typography component="span" variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#555', letterSpacing: '0.05em', lineHeight: 1.2 }}>
                  Current Boundary
                </Typography>
                <Box
                  data-testid="watershed-current-boundary"
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    px: 1.5,
                    py: '7px',
                    fontSize: '1rem',
                    whiteSpace: 'nowrap',
                    mt: '2px',
                  }}
                >
                  HUC{getHucLevelForZoom(zoom)}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                mt: 1,
                p: 1,
                pl: 0,
              }}
              data-testid="layer-source"
            >
              <Typography
                component="div"
                variant="caption"
                sx={{
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  color: '#555',
                  letterSpacing: '0.05em',
                  mb: -0.5,
                }}
              >
                Source
              </Typography>
              <a
                href={
                  'https://hydrowfs.nationalmap.gov/arcgis/rest/services/wbd/MapServer'
                }
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Data source: ${'USGS Watershed Boundary Dataset'}`}
                style={{
                  textDecoration: 'underline',
                  color: LINK_BLUE,
                  fontSize: '1rem',
                  wordBreak: 'break-word',
                }}
              >
                {'USGS Watershed Boundary Dataset'}
              </a>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
